import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import OrderStatusIndicator from "@/components/OrderStatusIndicator";
import { formatDate } from "@/lib/format";
import {
    Database,
    Plus,
    ArrowRight,
    Sprout,
    Calculator,
    Printer,
    Truck,
    Sprout as PlantIcon,
    MapPin,
    PieChart,
} from "lucide-react";

/**
 * Dashboard — operationeel, gericht op vandaag.
 *
 * Bovenste KPI-rij beantwoordt de 4 dagelijkse vragen:
 *   - Wat moet nog berekend worden?  → `todo_calc`
 *   - Wat moet nog geprint worden?    → `todo_print`
 *   - Hoe druk wordt vandaag?         → karren + planten + locaties
 *   - Zijn alle documenten klaar?     → `print_progress_today`
 *
 * Recente bestellingen tabel toont alleen de orders van vandaag (eerst) en
 * recente daarvoor, met inline status-indicators.
 */
export default function Dashboard() {
    const [stats, setStats] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const [s, o] = await Promise.all([api.get("/dashboard/stats"), api.get("/orders")]);
            setStats(s.data);
            // De orders-API kan zowel een lijst als een gepagineerd object teruggeven.
            // Normaliseer dit hier zodat het dashboard in beide omgevingen stabiel blijft.
            setOrders(
                Array.isArray(o.data)
                    ? o.data
                    : o.data?.orders || o.data?.items || []
            );
        } catch (e) {
            // 401 wordt door api.js interceptor afgehandeld (logt user uit + redirect).
            // Andere fouten: lege state, log voor debugging.
            console.warn("[dashboard] load failed", e.response?.status);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const seed = async () => {
        const hasData =
            (stats?.total_orders ?? 0) > 0 ||
            (stats?.active_products ?? 0) > 0;
        if (hasData) {
            const ok = window.confirm(
                "⚠ De database bevat al data. Klik OK om ALLES te WISSEN en te vervangen door voorbeelddata.\n\n" +
                "Dit kan niet ongedaan worden gemaakt. Annuleer als je je echte productdata wilt behouden."
            );
            if (!ok) return;
            const ok2 = window.confirm("Weet je het écht zeker? Alle producten, klanten, orders en berekeningen worden gewist.");
            if (!ok2) return;
        }
        try {
            await api.post(`/seed${hasData ? "?force=true" : ""}`);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail ?? "Seed mislukt"));
        }
    };

    const isEmpty = stats && stats.active_products === 0;
    const todayStr = stats?.today ? formatDate(stats.today) : "—";

    // Splits orders: vandaag eerst, dan rest
    const todayOrders = orders.filter((o) => o.delivery_date === stats?.today);
    const otherOrders = orders.filter((o) => o.delivery_date !== stats?.today);

    return (
        <div className="p-5 sm:p-7 lg:p-10 xl:p-12 max-w-[1480px] mx-auto pb-24 lg:pb-12">
            <PageHeader
                kicker={`Vandaag · ${todayStr}`}
                title="Dashboard"
                subtitle="Wat moet er vandaag nog gebeuren? Berekenen, printen en leveren — in 1 oogopslag."
                testid="dashboard-header"
                actions={
                    <>
                        {isEmpty && (
                            <button
                                data-testid="btn-seed"
                                onClick={seed}
                                className="h-10 px-4 rounded-xl border border-florefy-900/10 bg-white hover:bg-florefy-50 text-sm font-medium flex items-center gap-2 transition-all shadow-sm"
                            >
                                <Database className="h-4 w-4" /> Seed voorbeelddata
                            </button>
                        )}
                        <Link
                            to="/printcentrum"
                            data-testid="btn-go-print-center"
                            className="h-10 px-4 rounded-xl border border-florefy-900/10 bg-white hover:bg-florefy-50 text-sm font-medium flex items-center gap-2 transition-all shadow-sm"
                        >
                            <Printer className="h-4 w-4" /> Printcentrum
                        </Link>
                        <button
                            data-testid="btn-new-order-header"
                            onClick={() => navigate("/bestellingen/nieuw")}
                            className="h-10 px-4 rounded-xl bg-florefy-900 hover:bg-florefy-800 text-white text-sm font-semibold flex items-center gap-2 transition-all"
                        >
                            <Plus className="h-4 w-4" /> Nieuwe bestelling
                        </button>
                    </>
                }
            />

            {isEmpty && (
                <div
                    data-testid="empty-state"
                    className="mb-8 border border-dashed border-florefy-200 bg-white p-6 rounded-2xl flex items-center gap-5 shadow-sm"
                >
                    <Sprout className="h-8 w-8 text-florefy-700 shrink-0" />
                    <div className="flex-1">
                        <h3 className="text-base font-semibold text-ink-900">
                            Nog geen producten in het systeem
                        </h3>
                        <p className="text-sm text-ink-500 mt-1">
                            Klik op &laquo;Seed voorbeelddata&raquo; om realistische plantgegevens
                            en voorbeeldbestellingen te laden, of voeg zelf producten en
                            hoogteprofielen toe.
                        </p>
                    </div>
                </div>
            )}

            {/* KPI: Actie-vereist */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" data-testid="kpis-todo">
                <KpiCard
                    icon={Calculator}
                    label="Nog te berekenen"
                    value={stats?.todo_calc ?? "—"}
                    subtitle={`van ${stats?.today_orders ?? 0} bestellingen vandaag`}
                    accent={stats?.todo_calc > 0 ? "amber" : "neutral"}
                    href="/bestellingen?status=open"
                    testid="kpi-todo-calc"
                    loading={loading}
                />
                <KpiCard
                    icon={Printer}
                    label="Nog te printen"
                    value={stats?.todo_print ?? "—"}
                    subtitle={`${stats?.docs_printed_today ?? 0}/${stats?.docs_required_today ?? 0} labels vandaag`}
                    accent={stats?.todo_print > 0 ? "amber" : "emerald"}
                    href="/printcentrum"
                    testid="kpi-todo-print"
                    loading={loading}
                />
                <KpiCard
                    icon={PieChart}
                    label="Voortgang printen"
                    value={stats ? `${stats.print_progress_today ?? 0}%` : "—"}
                    subtitle={
                        stats?.print_progress_today === 100
                            ? "Alle documenten klaar voor de kas"
                            : `${Math.max(0, (stats?.docs_required_today ?? 0) - (stats?.docs_printed_today ?? 0))} labels open`
                    }
                    accent={stats?.print_progress_today === 100 ? "emerald" : "neutral"}
                    testid="kpi-progress"
                    loading={loading}
                />
            </div>

            {/* KPI: Volume vandaag */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-9" data-testid="kpis-volume">
                <KpiCard
                    icon={Truck}
                    label="Karren vandaag"
                    value={stats?.total_carts_today ?? "—"}
                    subtitle={`uit ${stats?.today_orders ?? 0} bestellingen`}
                    accent="neutral"
                    testid="kpi-carts-today"
                    loading={loading}
                />
                <KpiCard
                    icon={PlantIcon}
                    label="Planten vandaag"
                    value={stats?.total_units_today?.toLocaleString("nl-NL") ?? "—"}
                    subtitle="totaal aantal stuks"
                    accent="neutral"
                    testid="kpi-units-today"
                    loading={loading}
                />
                <KpiCard
                    icon={MapPin}
                    label="Actieve locaties"
                    value={stats?.active_locations_today ?? "—"}
                    subtitle="uniek vandaag in gebruik"
                    accent="neutral"
                    testid="kpi-locations-today"
                    loading={loading}
                />
            </div>

            {/* Recente bestellingen */}
            <div className="bg-white border border-florefy-900/10 rounded-2xl overflow-hidden shadow-[0_14px_40px_rgba(23,61,44,0.055)]">
                <div className="px-5 sm:px-6 py-5 border-b border-ink-100 flex items-center justify-between">
                    <h2 className="text-base font-bold text-ink-900">
                        Recente bestellingen
                        {todayOrders.length > 0 && (
                            <span className="ml-2 font-mono text-[10px] uppercase tracking-widest text-florefy-700">
                                · {todayOrders.length} vandaag
                            </span>
                        )}
                    </h2>
                    <Link
                        to="/bestellingen"
                        className="text-sm font-semibold text-florefy-700 hover:text-florefy-900 flex items-center gap-1.5 transition-colors"
                        data-testid="link-all-orders"
                    >
                        Alles bekijken <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                </div>
                <table className="hidden sm:table w-full text-sm" data-testid="recent-orders-table">
                    <thead>
                        <tr className="bg-ink-50">
                            <th className="text-left px-5 py-3 data-label">Klant</th>
                            <th className="text-left px-5 py-3 data-label">Leverdatum</th>
                            <th className="text-left px-5 py-3 data-label">Regels</th>
                            <th className="text-left px-5 py-3 data-label">Status</th>
                            <th className="px-5 py-3 data-label text-right">Actie</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.length === 0 && !loading && (
                            <tr>
                                <td colSpan={5} className="px-5 py-12 text-center text-ink-500">
                                    Nog geen bestellingen.
                                </td>
                            </tr>
                        )}
                        {[...todayOrders, ...otherOrders].slice(0, 10).map((o) => {
                            const isToday = o.delivery_date === stats?.today;
                            return (
                                <tr
                                    key={o.id}
                                    className={`border-t border-border/60 hover:bg-ink-50 transition-colors ${isToday ? "bg-florefy-50/30" : ""}`}
                                    data-testid={`order-row-${o.id}`}
                                >
                                    <td className="px-5 py-3 text-ink-900 font-medium">
                                        {o.customer?.name || "—"}
                                        {isToday && (
                                            <span className="ml-2 font-mono text-[9px] uppercase tracking-widest text-florefy-700">
                                                vandaag
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-ink-700" title={o.delivery_date}>
                                        {formatDate(o.delivery_date)}
                                    </td>
                                    <td className="px-5 py-3 font-mono text-ink-700">
                                        {o.lines.length}
                                    </td>
                                    <td className="px-5 py-3">
                                        <OrderStatusIndicator
                                            calculated={!!o.last_calculation_id}
                                            stale={!!o.is_stale}
                                            updated_at={o.updated_at}
                                            printed_status={o.labels_printed_status}
                                            printed_count={o.labels_printed_count}
                                            total_count={o.labels_total_count}
                                            testid={`order-status-${o.id}`}
                                        />
                                    </td>
                                    <td className="px-5 py-3 text-right">
                                        <Link
                                            to={`/bestellingen/${o.id}`}
                                            className="text-florefy-900 hover:text-florefy-700 text-sm"
                                            data-testid={`btn-view-order-${o.id}`}
                                        >
                                            Open →
                                        </Link>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {/* Mobile: card-list */}
                <div className="sm:hidden divide-y divide-border" data-testid="recent-orders-mobile">
                    {orders.length === 0 && !loading && (
                        <div className="px-5 py-12 text-center text-ink-500 text-sm">
                            Nog geen bestellingen.
                        </div>
                    )}
                    {[...todayOrders, ...otherOrders].slice(0, 10).map((o) => {
                        const isToday = o.delivery_date === stats?.today;
                        return (
                            <Link
                                key={o.id}
                                to={`/bestellingen/${o.id}`}
                                data-testid={`order-card-${o.id}`}
                                className={`block px-4 py-3 active:bg-ink-50 ${isToday ? "bg-florefy-50/30" : ""}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="font-medium text-ink-900 truncate text-sm">
                                            {o.customer?.name || "—"}
                                        </div>
                                        <div className="text-xs text-ink-500 font-mono mt-0.5">
                                            {formatDate(o.delivery_date)} · {o.lines.length} regel{o.lines.length === 1 ? "" : "s"}
                                            {isToday && <span className="ml-2 text-florefy-700">VANDAAG</span>}
                                        </div>
                                    </div>
                                    <div className="text-ink-300 shrink-0">→</div>
                                </div>
                                <div className="mt-2">
                                    <OrderStatusIndicator
                                        calculated={!!o.last_calculation_id}
                                        stale={!!o.is_stale}
                                        updated_at={o.updated_at}
                                        printed_status={o.labels_printed_status}
                                        printed_count={o.labels_printed_count}
                                        total_count={o.labels_total_count}
                                        testid={`order-status-mobile-dash-${o.id}`}
                                    />
                                </div>
                            </Link>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

const ACCENT_CLS = {
    amber: {
        border: "border-amber-200/80",
        bg: "bg-gradient-to-br from-amber-50 to-white",
        text: "text-amber-900",
        iconBg: "bg-amber-100/80",
        iconText: "text-amber-700",
    },
    emerald: {
        border: "border-emerald-200/80",
        bg: "bg-gradient-to-br from-emerald-50 to-white",
        text: "text-emerald-900",
        iconBg: "bg-emerald-100/80",
        iconText: "text-emerald-700",
    },
    neutral: {
        border: "border-florefy-900/10",
        bg: "bg-white",
        text: "text-ink-900",
        iconBg: "bg-florefy-50",
        iconText: "text-florefy-700",
    },
};

function KpiCard({ icon: Icon, label, value, subtitle, accent = "neutral", href, testid, loading }) {
    const t = ACCENT_CLS[accent] || ACCENT_CLS.neutral;
    const inner = (
        <div
            data-testid={testid}
            className={`relative overflow-hidden ${t.bg} ${t.border} border rounded-2xl p-5 min-h-[138px] transition-all duration-200 shadow-[0_8px_28px_rgba(23,61,44,0.045)] hover:-translate-y-0.5 hover:shadow-[0_14px_34px_rgba(23,61,44,0.085)]`}
        >
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-florefy-300/40 to-transparent" />
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="data-label">{label}</div>
                    <div className={`text-[2rem] font-display font-bold tracking-[-0.04em] mt-2 ${t.text}`} data-testid={`${testid}-value`}>
                        {loading ? "…" : value}
                    </div>
                    {subtitle && (
                        <div className="text-xs leading-relaxed text-ink-500 mt-1.5">
                            {subtitle}
                        </div>
                    )}
                </div>
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${t.iconBg}`}>
                    <Icon className={`h-5 w-5 ${t.iconText}`} />
                </div>
            </div>
        </div>
    );
    if (href) {
        return (
            <Link to={href} className="block group" data-testid={`${testid}-link`}>
                {inner}
            </Link>
        );
    }
    return inner;
}
